import { useCallback, useMemo, useState } from "react";

export type Pagination = {
  offset: number;
  limit: number;
};

export const toQuery = (p: Pagination) => ({
  limit: p.limit.toString(),
  offset: p.offset.toString(),
});

/**
 * When `controlled` is passed, the current page lives with the caller (e.g. a page
 * keeping it in the URL) and every page change is reported instead of stored. Omit it
 * and the page is held internally, which is what most callers want.
 */
export type PaginationControl = { page: number; onPageChange: (page: number) => void };

export const usePagination = (init: Pagination = { offset: 0, limit: 20 }, controlled?: PaginationControl) => {
  const [limit, setLimit] = useState(init.limit);
  const [internalOffset, setInternalOffset] = useState(init.offset);

  const onPageChange = controlled?.onPageChange;
  const offset = controlled ? Math.max((controlled.page - 1) * limit, 0) : internalOffset;
  const page = Math.floor(offset / limit) + 1;

  const setPage = useCallback(
    (next: number) => {
      if (onPageChange) onPageChange(Math.max(next, 1));
      else setInternalOffset(Math.max((next - 1) * limit, 0));
    },
    [limit, onPageChange],
  );

  const nextPage = useCallback(() => setPage(page + 1), [page, setPage]);
  const prevPage = useCallback(() => setPage(page - 1), [page, setPage]);

  const setPageSize = useCallback(
    (size: number) => {
      if (size === 0) return;
      setLimit(size);
      // A new page size invalidates the current offset, so go back to the first page.
      if (onPageChange) onPageChange(1);
      else setInternalOffset(0);
    },
    [onPageChange],
  );

  const pagination = useMemo(() => ({ limit, offset }), [limit, offset]);

  return {
    pagination,
    page,
    nextPage,
    prevPage,
    setPage,
    setPageSize,
  };
};
